import React, { useState } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import {
  ArrowRightIcon, CheckIcon, CreditCardIcon, EnvelopeIcon, ExclamationCircleIcon, EyeIcon, EyeSlashIcon,
  LockClosedIcon, TrashIcon,
} from '@heroicons/react/24/outline'
import { KORZYSCI_KONTA } from '../components/auth/korzysciKonta'
import { errorText, hasPendingFlow } from '../api/auth'
import { odczytajPowrot, wyczyscPowrot } from '../lib/powrotPoRejestracji'
import { useAuth } from '../context/AuthContext'
import useDocumentTitle from '../hooks/useDocumentTitle'

/**
 * Logowanie `/logowanie`, nowy wygląd 2026-10-19 (prośba właściciela): ten sam układ co rejestracja (`Signup.jsx`):
 * formularz w karcie, obok zachęta do założenia konta dla osób, które go nie mają (dawny `KafelekRejestracji`,
 * decyzja z 2026-08-11). Logika logowania bez zmian.
 */

const poleKlasa = (blad) =>
  `h-[50px] w-full rounded-xl border-[1.5px] bg-white text-[15px] text-gray-900 placeholder:text-gray-400 transition
   focus:border-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-600/15 disabled:bg-gray-50
   ${blad ? 'border-red-600' : 'border-gray-300'}`

const Signin = () => {
  useDocumentTitle('Logowanie')
  const { login, isAuthenticated, loading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [form, setForm] = useState({ email: '', password: '' })
  const [pokaz, setPokaz] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [needsVerification, setNeedsVerification] = useState(false)

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))

  // Po zalogowaniu na pulpit (2026-10-11); `/pulpit` sam odsyła konto prywatne na stronę główną.
  if (!loading && isAuthenticated) return <Navigate to="/pulpit" replace />

  const submit = async (e) => {
    e.preventDefault()
    setError('')
    setNeedsVerification(false)
    setBusy(true)
    const res = await login({ email: form.email.trim(), password: form.password })
    setBusy(false)

    if (res.meta && res.meta.is_authenticated) {
      // Wracamy tam, skąd użytkownik został odesłany do logowania, a po świeżej rejestracji tam, skąd poszedł ją
      // założyć (lib/powrotPoRejestracji; potwierdzenie e-maila w innej karcie gubi `state`).
      const cel = location.state?.from || odczytajPowrot() || '/pulpit'
      wyczyscPowrot()
      navigate(cel, { replace: true })
      return
    }
    // Hasło poprawne, ale adres niepotwierdzony: zupełnie inna sytuacja niż złe hasło i wymaga innej podpowiedzi,
    // nie komunikatu o błędzie.
    if (hasPendingFlow(res, 'verify_email')) {
      setNeedsVerification(true)
      return
    }
    setError(errorText(res, 'Nie udało się zalogować.'))
  }

  return (
    <div className="bg-gray-50 text-gray-900">
      <div className="mx-auto flex max-w-[1080px] flex-col gap-10 px-4 pb-12 pt-8 sm:px-6 sm:pt-14 lg:flex-row-reverse lg:items-start lg:justify-center lg:gap-16">

        <section aria-labelledby="tytul-logowania"
                 className="w-full min-w-0 rounded-[20px] border border-gray-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_12px_32px_rgba(15,23,42,0.06)] sm:p-8 lg:max-w-[460px] lg:flex-[0_1_460px]">
          <form onSubmit={submit} noValidate className="flex flex-col gap-5">
            <div className="flex flex-col gap-1">
              <h1 id="tytul-logowania" className="m-0 text-2xl font-bold">Zaloguj się</h1>
              <p className="m-0 text-sm text-gray-500">Witaj ponownie w VoltValue.</p>
            </div>

            {error && (
              <p role="alert" className="m-0 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />{error}
              </p>
            )}

            {needsVerification && (
              <p role="alert" className="m-0 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm leading-6 text-amber-900">
                <EnvelopeIcon className="mt-0.5 h-5 w-5 flex-shrink-0" aria-hidden="true" />
                <span>
                  Ten adres nie został jeszcze potwierdzony. Sprawdź skrzynkę (także folder <strong>spam</strong>) i otwórz link
                  z wiadomości rejestracyjnej.
                </span>
              </p>
            )}

            <div className="flex flex-col gap-1.5">
              <label htmlFor="log-email" className="text-sm font-semibold">Adres e-mail</label>
              <div className="relative">
                <EnvelopeIcon className="pointer-events-none absolute left-3.5 top-[15px] h-5 w-5 text-gray-400" aria-hidden="true" />
                <input id="log-email" type="email" required autoComplete="email" placeholder="jan@firma.pl"
                       value={form.email} onChange={set('email')} disabled={busy} className={`${poleKlasa(Boolean(error))} px-11`} />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex items-baseline justify-between gap-3">
                <label htmlFor="log-haslo" className="text-sm font-semibold">Hasło</label>
                <Link to="/reset-hasla" className="text-[13px] font-semibold text-blue-600 no-underline hover:text-blue-700">Nie pamiętam hasła</Link>
              </div>
              <div className="relative">
                <LockClosedIcon className="pointer-events-none absolute left-3.5 top-[15px] h-5 w-5 text-gray-400" aria-hidden="true" />
                <input id="log-haslo" type={pokaz ? 'text' : 'password'} required autoComplete="current-password"
                       value={form.password} onChange={set('password')} disabled={busy}
                       className={`${poleKlasa(Boolean(error))} pl-11 pr-[52px]`} />
                <button type="button" onClick={() => setPokaz((p) => !p)} aria-label={pokaz ? 'Ukryj hasło' : 'Pokaż hasło'} aria-pressed={pokaz}
                        className="absolute right-[3px] top-[3px] flex h-11 w-11 items-center justify-center rounded-[10px] text-gray-600 hover:bg-gray-100">
                  {pokaz ? <EyeSlashIcon className="h-5 w-5" aria-hidden="true" /> : <EyeIcon className="h-5 w-5" aria-hidden="true" />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={busy}
                    className="mt-1 flex h-[52px] w-full items-center justify-center gap-2.5 rounded-xl bg-blue-600 text-base font-semibold text-white shadow-[0_6px_16px_rgba(37,99,235,0.28)] transition hover:bg-blue-700 disabled:cursor-wait disabled:opacity-70">
              {busy ? 'Loguję…' : <>Zaloguj się<ArrowRightIcon className="h-[18px] w-[18px]" strokeWidth={2.2} aria-hidden="true" /></>}
            </button>
          </form>
        </section>

        <section aria-labelledby="nowe-konto" className="flex min-w-0 flex-col gap-7 lg:flex-[1_1_400px] lg:pt-2">
          <div className="flex flex-col gap-3.5">
            <span className="inline-flex items-center gap-2 self-start rounded-full bg-blue-50 px-3 py-1.5 text-[13px] font-semibold text-blue-700">
              <CheckIcon className="h-[15px] w-[15px]" strokeWidth={2.4} aria-hidden="true" />Konto jest darmowe
            </span>
            <h2 id="nowe-konto" className="m-0 max-w-[460px] text-[24px] font-bold leading-[1.2] tracking-[-0.01em] sm:text-[28px]">
              Nie masz jeszcze konta?
            </h2>
            <p className="m-0 max-w-[440px] text-base leading-relaxed text-gray-600">Wystarczy adres e-mail i hasło.</p>
          </div>
          <ul className="m-0 flex list-none flex-col gap-[18px] p-0">
            {KORZYSCI_KONTA.map(({ Icon, tytul, opis }) => (
              <li key={tytul} className="flex items-start gap-3.5">
                <span aria-hidden="true" className="flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-blue-600">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="flex flex-col gap-0.5">
                  <strong className="text-[15px] font-semibold">{tytul}</strong>
                  <span className="text-sm leading-normal text-gray-500">{opis}</span>
                </span>
              </li>
            ))}
          </ul>
          <div className="flex flex-col gap-4">
            <Link to="/rejestracja" state={location.state?.from ? { from: location.state.from } : undefined}
                  className="flex h-[52px] items-center justify-center gap-2 self-stretch rounded-xl border-[1.5px] border-gray-300 bg-white px-6 text-base font-semibold text-gray-900 no-underline hover:border-gray-400 sm:self-start">
              Załóż darmowe konto<ArrowRightIcon className="h-[18px] w-[18px]" strokeWidth={2.2} aria-hidden="true" />
            </Link>
            <div className="flex flex-wrap gap-x-[22px] gap-y-2.5 border-t border-gray-200 pt-5 text-[13px] text-gray-600">
              <span className="inline-flex items-center gap-2"><CreditCardIcon className="h-[18px] w-[18px]" aria-hidden="true" />Bez karty płatniczej</span>
              <span className="inline-flex items-center gap-2"><TrashIcon className="h-[18px] w-[18px]" aria-hidden="true" />Konto usuniesz jednym kliknięciem</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

export default Signin
